import { useEntities } from "@/api/core";
import {
  importAccounts,
  importAccountsFromStatuses,
} from "@/api/core/client/importers/account-importer";
import { importGroups } from "@/api/core/client/importers/group-importer";
import { importStatuses } from "@/api/core/client/importers/status-importer";
import {
  type Account,
  type Group,
  type Status,
  accountSchema,
  groupSchema,
  statusSchema,
  tagSchema,
} from "@/schemas";

import { SearchQueryKeys } from "../query-keys";

import type { EntitySchema } from "@/api/core/types";

enum SearchTypes {
  ACCOUNTS = "accounts",
  HASHTAGS = "hashtags",
  GROUPS = "groups",
  STATUSES = "statuses",
}

function getEntityMeta(type: SearchTypes) {
  let schema, responseKey;

  switch (type) {
    case SearchTypes.ACCOUNTS:
      schema = accountSchema;
      responseKey = "accounts";
      break;
    case SearchTypes.GROUPS:
      schema = groupSchema;
      responseKey = "groups";
      break;
    case SearchTypes.HASHTAGS:
      schema = tagSchema;
      responseKey = "hashtags";
      break;
    case SearchTypes.STATUSES:
      schema = statusSchema;
      responseKey = "statuses";
      break;
  }

  return {
    schema: schema as EntitySchema,
    responseKey,
  };
}

function useSearch(q: string = "", type: SearchTypes, limit: number = 20) {
  const { schema, responseKey } = getEntityMeta(type);

  return useEntities({
    queryKey: SearchQueryKeys.all(q, type),
    enabled: q.length > 0,
    responseKey,
    schema,
    endpoint: "/api/v2/search",
    paginationType: "offset",
    params: {
      q,
      limit,
      resolve: true,
      type,
    },
    onSuccess(result) {
      switch (type) {
        case SearchTypes.ACCOUNTS:
          importAccounts(result as Account[]);
          break;
        case SearchTypes.GROUPS:
          importGroups(result as Group[]);
          break;
        case SearchTypes.STATUSES:
          importStatuses(result as Status[]);
          importAccountsFromStatuses(result as Status[]);
          break;
      }
    },
    relationshipConfig: {
      category: "account",
      extractIds(result) {
        let accountIds: string[] = [];
        if (type === SearchTypes.ACCOUNTS) {
          accountIds = result.map((entity) => entity.id);
        } else if (type === SearchTypes.STATUSES) {
          accountIds = (result as Status[]).map((entity) => entity.account.id);
        }
        return accountIds;
      },
    },
  });
}

export { useSearch, SearchTypes };
